
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { fetchUserSubscriptions, createSubscription } from '@/lib/supabase';
import { SubscriptionType } from '@/lib/supabase/types';

export const useSubscriptions = (userId: string | undefined) => {
  const { t } = useTranslation();
  const [subscriptions, setSubscriptions] = useState<SubscriptionType[]>([]);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  
  useEffect(() => {
    if (userId) {
      fetchSubscriptions(userId);
    } else {
      console.warn("[useSubscriptions] No user ID provided, cannot fetch subscriptions");
      setIsLoadingSubscriptions(false);
    }
  }, [userId]);
  
  const fetchSubscriptions = async (userId: string) => {
    if (!userId) {
      console.error("[useSubscriptions] No user ID provided for fetching subscriptions");
      return;
    }
    
    try {
      setIsLoadingSubscriptions(true);
      console.log("[useSubscriptions] Fetching subscriptions for user:", userId);
      const userSubscriptions = await fetchUserSubscriptions(userId);
      setSubscriptions(userSubscriptions);
    } catch (error) {
      console.error("[useSubscriptions] Error fetching subscriptions:", error);
      toast.error("Failed to load subscriptions", {
        description: "There was a problem loading your subscriptions. Please try again later.",
      });
    } finally {
      setIsLoadingSubscriptions(false);
    }
  };
  
  const handleCreateSubscription = async () => {
    if (!userId) {
      toast.error("Cannot create subscription", { 
        description: "User authentication is required to create a subscription." 
      });
      return;
    }
    
    setIsCreating(true);
    
    try {
      console.log("[useSubscriptions] Creating subscription for user:", userId);
      const result = await createSubscription(userId);
      
      if (result.success) {
        toast.success(t("dashboard.subscriptions.createdTitle"), {
          description: t("dashboard.subscriptions.createdDesc"),
        });
        fetchSubscriptions(userId);
      } else {
        console.error("[useSubscriptions] Failed to create subscription:", result.error);
        toast.error(t("dashboard.subscriptions.createErrorTitle"), {
          description: result.error || t("dashboard.subscriptions.createErrorDesc"),
        });
      }
    } catch (error) {
      console.error("[useSubscriptions] Error creating subscription:", error);
      toast.error(t("dashboard.subscriptions.createErrorTitle"), {
        description: t("dashboard.subscriptions.unexpectedError"),
      });
    } finally {
      setIsCreating(false);
    }
  };
  
  return {
    subscriptions,
    isLoadingSubscriptions,
    isCreating,
    fetchSubscriptions,
    handleCreateSubscription
  };
};
